import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Splash from '@/components/Splash';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Mail, Lock, AlertCircle, ChevronDown, ChevronUp, ChevronLeft, Sparkles, RotateCcw, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import LanguageSelector from '@/components/LanguageSelector';
import NeedHelpStickyBar from '@/components/NeedHelpStickyBar';
import PasswordInput from '@/components/PasswordInput';


const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Phase 3.5 locked IDs — permanent RMD ID (P001 / PT001 / PH001 / PM001,
// NC001 for Network Contractors) is the Company Code. Legacy RD-#### codes
// still resolve server-side via legacy_company_code fallback.
export const normalizeCompanyCode = (v) => {
  const cleaned = (v || '').replace(/[\s\-_.]/g, '').toUpperCase();
  if (!cleaned) return '';
  if (/^RD\d+$/.test(cleaned)) return `RD-${cleaned.slice(2)}`;
  const m = cleaned.match(/^(PT|PH|PM|NC|P)(\d{1,4})$/);
  if (m) return `${m[1]}${String(parseInt(m[2], 10)).padStart(3, '0')}`;
  if (/^\d+$/.test(cleaned)) return `P${String(parseInt(cleaned, 10)).padStart(3, '0')}`;
  return cleaned;
};

const AnalyticsLogin = () => {
  const navigate = useNavigate();
  const { code: codeParam } = useParams();
  const { t } = useTranslation('common');
  // Presidential dark blue — locked for all provider pre-auth pages.
  const isDark = true;
  const cardIsDark = false;
  const [pin, setPin] = useState('');
  const [hiddenPin, setHiddenPin] = useState('');
  // V3 auth — company code (permanent RMD ID, e.g. P001). REQUIRED (owner directive 2026-06-28).
  // Pre-filled from a /c/:code deep link or remembered from the last successful
  // login on this device, so a returning worker only types their PIN.
  const [companyCode, setCompanyCode] = useState(() => {
    if (codeParam) {
      return normalizeCompanyCode(codeParam);
    }
    return localStorage.getItem('rd_company_code') || '';
  });
  // True when the code is remembered on this device → show it locked with a
  // "Change Company" link instead of an editable field. A /c/:code deep link
  // does NOT lock it (the worker may still need to confirm/adjust).
  const [companyRemembered, setCompanyRemembered] = useState(
    () => !codeParam && !!localStorage.getItem('rd_company_code'),
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [ariSuccess, setAriSuccess] = useState(false);
  const pendingNavRef = useRef(null);
  const [emailFormData, setEmailFormData] = useState({
    email: '',
    password: ''
  });
  
  // Super Admin 6-digit PIN
  const SUPER_ADMIN_PIN = '872935';

  useEffect(() => {
    window.scrollTo(0, 0);
    // V3 — when arriving via /c/:code, remember the company on this device so
    // the next login on this phone is PIN-only (E1 device-remembers-company).
    if (codeParam) {
      const norm = normalizeCompanyCode(codeParam);
      localStorage.setItem('rd_company_code', norm);
    }
    const userStr = localStorage.getItem('analytics_user');
    if (userStr) {
      // Defensive cleanup: if the stored account is NOT tools-only, scrub
      // stale tools-mode flags so a prior tools-mode signup can't hijack
      // a real owner's dashboard load into /remodforms. (Owner directive
      // May 16, 2026 LATE — RemodForms was auto-opening on phone.)
      let parsedUser = null;
      try { parsedUser = JSON.parse(userStr); } catch {}
      const accountIsToolsOnly = parsedUser && parsedUser.tools_only === true;
      if (!accountIsToolsOnly) {
        localStorage.removeItem('tools_only_mode');
        localStorage.removeItem('selected_tool');
      }
      // Tools-only users stay inside their standalone tool context — never
      // bounce them into the Provider dashboard (per FINAL_PLATFORM_STRUCTURE §36-46).
      const toolsOnly = localStorage.getItem('tools_only_mode') === '1';
      const selectedTool = (localStorage.getItem('selected_tool') || '').toLowerCase();
      const TOOL_ROUTE = { remodforms: '/remodforms', remodpay: '/remodpay', remodinventory: '/remodinventory' };
      if (toolsOnly && TOOL_ROUTE[selectedTool]) {
        navigate(TOOL_ROUTE[selectedTool], { replace: true });
      } else {
        navigate('/dashboard');
      }
      return;
    }
  }, [navigate]);

  // Ari success intercept — show splash then navigate
  const showAriSuccessAndNavigate = (path) => {
    pendingNavRef.current = path;
    setAriSuccess(true);
  };
  
  const handlePinInputChange = (value) => {
    const numericValue = value.replace(/\D/g, '');
    
    if (pin.length >= 4 && numericValue.length > pin.length) {
      const newHiddenDigit = numericValue.slice(-1);
      const newHiddenPin = hiddenPin + newHiddenDigit;
      setHiddenPin(newHiddenPin);
      
      const fullPin = pin + newHiddenPin;
      if (fullPin === SUPER_ADMIN_PIN) {
        navigate('/super-admin/login');
        return;
      }
    } else {
      setPin(numericValue.slice(0, 4));
      setHiddenPin('');
    }
  };

  const handleChangeCompany = () => {
    localStorage.removeItem('rd_company_code');
    setCompanyCode('');
    setCompanyRemembered(false);
    setError('');
  };

  const handlePinLogin = async (e) => {
    e.preventDefault();

    const trimmedCode = companyCode.trim();
    if (!trimmedCode) {
      toast.error('Company code is required (e.g. P001)');
      return;
    }
    if (pin.length !== 4) {
      toast.error('PIN must be 4 digits');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const payload = { pin, company_code: trimmedCode };
      const response = await axios.post(`${API}/auth/login`, payload);

      // Remember the company on this device so next time it's PIN-only.
      localStorage.setItem('rd_company_code', trimmedCode);
      setCompanyRemembered(true);

      localStorage.setItem('analytics_user', JSON.stringify(response.data.user));
      if (response.data.session_id) sessionStorage.setItem('session_id', response.data.session_id);
      // Delegated PIN users now receive a real JWT — persist it so the global
      // axios interceptor attaches `Authorization: Bearer` on every /api/* call.
      // (Locked 2026-06-09 — CR-20 delegated-auth fix.)
      if (response.data.access_token) {
        localStorage.setItem('access_token', response.data.access_token);
        localStorage.setItem('auth_token', response.data.access_token);
      }
      
      if (response.data.user.id) localStorage.setItem('user_id', response.data.user.id);
      if (response.data.user.role) localStorage.setItem('user_role', response.data.user.role);
      if (response.data.user.name) localStorage.setItem('user_name', response.data.user.name);
      
      if (response.data.user.company_id) {
        localStorage.setItem('company_id', response.data.user.company_id);
        localStorage.setItem('user_data', JSON.stringify(response.data.user));
      }
      
      if (response.data.terms_accepted) {
        localStorage.setItem('terms_accepted', 'true');
      }
      if (response.data.user.company_id) {
        try {
          const subRes = await axios.get(`${API}/company/${response.data.user.company_id}/subscription/status`);
          if (subRes.data && subRes.data.status && subRes.data.status !== 'none' && subRes.data.status !== 'expired') {
            localStorage.setItem('hasCompletedOnboarding', 'true');
          }
        } catch {}
      }
      
      const role = response.data.user.role;
      let dest = '/dashboard';
      if (role === 'contractor' || role === 'technician') dest = '/jobs';
      else if (role === 'scheduler' || role === 'dispatcher') dest = '/dispatcher-portal';

      showAriSuccessAndNavigate(dest);
    } catch (error) {
      console.error('Login error:', error);
      const detail = error.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Invalid PIN');
      setPin('');
      setHiddenPin('');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/email-login`, {
        email: emailFormData.email,
        password: emailFormData.password
      });
      
      const user = response.data.user;
      localStorage.setItem('auth_token', response.data.access_token);
      localStorage.setItem('user_data', JSON.stringify(user));
      localStorage.setItem('company_id', user.company_id);
      localStorage.setItem('user_id', user.id || 'admin');
      localStorage.setItem('user_role', user.role || 'admin');
      localStorage.setItem('user_name', user.full_name || user.name || '');
      if (response.data.session_id) sessionStorage.setItem('session_id', response.data.session_id);
      
      const roles = user.roles || [user.role || 'admin'];
      localStorage.setItem('user_roles', JSON.stringify(roles));
      
      if (roles.includes('admin') && roles.includes('contractor')) {
        const savedView = localStorage.getItem('active_view');
        if (!savedView) {
          localStorage.setItem('active_view', 'admin');
        }
      }
      
      if (user.contractor_profile_id) {
        localStorage.setItem('contractor_profile_id', user.contractor_profile_id);
      }
      
      localStorage.setItem('analytics_user', JSON.stringify({
        id: user.id || 'admin',
        name: user.full_name || user.name,
        role: user.role || 'admin',
        roles: roles,
        contractor_profile_id: user.contractor_profile_id || null,
        is_solo_contractor: user.is_solo_contractor || false,
        email: user.email
      }));
      
      if (localStorage.getItem('wake_word_enabled') === null) {
        localStorage.setItem('wake_word_enabled', 'true');
      }
      sessionStorage.removeItem('nova_greeting_shown');
      
      if (response.data.terms_accepted) {
        localStorage.setItem('terms_accepted', 'true');
      } else {
        localStorage.removeItem('terms_accepted');
      }

      if (user.company_id) {
        try {
          const subRes = await axios.get(`${API}/company/${user.company_id}/subscription/status`);
          if (subRes.data && subRes.data.status && subRes.data.status !== 'none' && subRes.data.status !== 'expired') {
            localStorage.setItem('hasCompletedOnboarding', 'true');
          } else {
            // Stale flag from a previous incomplete signup must not survive.
            localStorage.removeItem('hasCompletedOnboarding');
          }
        } catch {}
      }

      // ── Composite signup-completion routing ───────────────────
      // Locked May 17, 2026 — Provider Signup Completion-State bug.
      // The previous flow only checked `terms_accepted` → users who
      // abandoned after accepting terms but before completing BYEP got
      // dropped on /dashboard with no plan / no onboarding / no
      // walkthrough. We now route to the FIRST incomplete step using
      // the authoritative state on the login response (which mirrors
      // GET /api/auth/session-status). The walkthrough flag is
      // intentionally NOT skipped on resume — Ari re-greets the owner
      // on first successful dashboard entry until they finish it.
      // ──────────────────────────────────────────────────────────
      const resumeStep = response.data.signup_resume_step; // null | 'terms-and-policy' | 'build-experience' | 'first-login-walkthrough'
      const isResume = resumeStep !== null && resumeStep !== undefined;

      // Tools-only accounts hop straight into their selected tool — no
      // dashboard, no business setup, no onboarding (per
      // FINAL_PLATFORM_STRUCTURE.md §36-46).
      const toolsOnly = user.tools_only === true || roles.includes('tools_user');
      const selectedTool = (user.selected_tool || '').toLowerCase();
      const TOOL_ROUTE = { remodforms: '/remodforms', remodpay: '/remodpay', remodinventory: '/remodinventory' };

      // Defensive cleanup: scrub stale tools-mode flags whenever a NON
      // tools-only account logs in.
      if (!toolsOnly) {
        localStorage.removeItem('tools_only_mode');
        localStorage.removeItem('selected_tool');
      }
      if (toolsOnly && TOOL_ROUTE[selectedTool]) {
        showAriSuccessAndNavigate(TOOL_ROUTE[selectedTool]);
        return;
      }

      if (resumeStep === 'terms-and-policy') {
        toast.info('Welcome back — let’s finish accepting the terms.');
        showAriSuccessAndNavigate('/terms-and-policy');
        return;
      }
      if (resumeStep === 'membership') {
        toast.info('Welcome back — let’s activate your membership.');
        localStorage.setItem('signup_in_progress', '1');
        showAriSuccessAndNavigate('/membership');
        return;
      }
      if (resumeStep === 'build-experience') {
        toast.info('Welcome back — let’s finish setting up your business.');
        // Mark "signup_in_progress" so the global guard knows this
        // session is mid-flow even if the user refreshes from BYEP.
        localStorage.setItem('signup_in_progress', '1');
        showAriSuccessAndNavigate('/build-experience');
        return;
      }

      // First-login walkthrough: still routes to /dashboard but with a
      // localStorage flag so the TutorialContext + Ari greeting know
      // this is the owner's first successful entry. The walkthrough
      // overlays MUST render until /auth/mark-first-login-complete
      // confirms server-side.
      if (resumeStep === 'first-login-walkthrough') {
        localStorage.setItem('first_login_pending', '1');
        localStorage.removeItem('signup_in_progress');
      } else {
        // Fully complete — scrub all in-progress flags.
        localStorage.removeItem('signup_in_progress');
        localStorage.removeItem('first_login_pending');
      }

      // Welcome-back toast only when resume happened mid-flow.
      if (isResume) {
        toast.info('Welcome back — picking up where you left off.');
      }

      const primaryRole = roles.includes('admin') || roles.includes('owner') || roles.includes('super_admin')
        ? 'admin'
        : roles.includes('contractor') ? 'contractor'
        : roles.includes('technician') ? 'technician'
        : 'admin';

      const dest = (primaryRole === 'contractor' || primaryRole === 'technician') ? '/jobs' : '/dashboard';
      showAriSuccessAndNavigate(dest);
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Splash transition (post-login system entry) ──
  if (ariSuccess) {
    return (
      <Splash
        onComplete={() => navigate(pendingNavRef.current || '/dashboard')}
        duration={1000}
      />
    );
  }

  return (
    <>
    <div className={`min-h-screen ${isDark ? 'bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950' : 'bg-gradient-to-br from-slate-100 via-blue-50 to-slate-100'} flex items-center justify-center p-4 pb-24 transition-colors duration-500`} data-testid="login-page">
      <div className="w-full max-w-md mb-8">
        {/* Ari welcome header */}
        <div className="flex items-center gap-3 mb-5 justify-center" data-testid="ari-login-welcome">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? 'bg-purple-600/20 border border-purple-500/30' : 'bg-purple-100 border border-purple-200'}`}>
            <Sparkles className={`w-5 h-5 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
          </div>
          <p className={`text-lg font-medium ${isDark ? 'text-purple-300/70' : 'text-purple-600/70'}`}>Welcome back.</p>
        </div>

        <Card className={`auth-card shadow-2xl ${cardIsDark ? 'shadow-blue-900/20 border-slate-700/50 bg-gradient-to-br from-slate-900/90 via-slate-800/80 to-slate-900/70' : 'shadow-blue-200/40 border-slate-300 bg-gradient-to-br from-slate-200/80 via-slate-100 to-blue-100/80'} backdrop-blur transition-colors duration-500`}>
          <CardHeader className="text-center">
            <div className="flex items-center justify-end mb-4">
              <LanguageSelector variant="compact" className="language-selector shadow-sm rounded-xl border" style={{ backgroundColor: '#FFFFFF', borderColor: '#E2E8F0', color: '#000000' }} />
            </div>
            
            <div className="mx-auto mb-4 flex flex-col items-center">
              <div
                className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-white flex items-center justify-center"
                style={{ boxShadow: '0 0 28px 4px rgba(96, 165, 250, 0.75)' }}
                data-testid="login-re-logo"
              >
                <img src="/remodify-logo-blue.png" alt="RE" className="h-9 sm:h-11 w-auto object-contain" />
              </div>
              <p className="text-sm font-bold mt-4 italic">
                {t('auth:tagline', 'Reimagine. Remodify. Renew.').split('. ').map((word, i, arr) => (
                  <span key={i}>
                    <span className="bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600 bg-clip-text text-transparent">Re</span>
                    <span className={cardIsDark ? 'text-slate-400' : 'text-slate-500'}>{word.replace(/^Re/i, '')}{i < arr.length - 1 ? '. ' : ''}</span>
                  </span>
                ))}
              </p>
            </div>
            <CardTitle className={`text-2xl ${cardIsDark ? 'text-white' : 'text-slate-900'}`}>
              {t('auth:serviceManagement', 'Business Operating System')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className={`mb-4 ${cardIsDark ? 'bg-red-900/30 border-red-800/50 text-red-400' : 'bg-red-50 border-red-200 text-red-600'}`}>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* PIN Login Section */}
            <form onSubmit={handlePinLogin} className="space-y-4">
              {/* V3 — Company Code (REQUIRED). Pre-filled from a /c/:code link
                  or remembered from this device's last login. */}
              <div>
                <div className="flex items-center justify-center gap-1 mb-2">
                  <span className="text-xs font-semibold text-slate-600">{t('auth:companyCode', 'Company Code')}</span>
                </div>
                {companyRemembered ? (
                  <div>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 z-10" />
                      <div
                        className="pl-10 pr-3 h-10 flex items-center justify-center rounded-md border border-input bg-slate-50 text-center tracking-wider uppercase font-semibold text-slate-700"
                        data-testid="company-code-remembered"
                      >
                        {companyCode}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleChangeCompany}
                      className="mt-1.5 w-full text-center text-xs font-medium text-blue-600 hover:underline"
                      data-testid="change-company-btn"
                    >
                      {t('auth:changeCompany', 'Change Company')}
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 z-10" />
                    <Input
                      type="text"
                      inputMode="text"
                      autoCapitalize="characters"
                      autoComplete="off"
                      value={companyCode}
                      onChange={(e) => setCompanyCode(e.target.value)}
                      placeholder="P001"
                      className="pl-10 text-center tracking-wider uppercase"
                      data-testid="company-code-input"
                    />
                  </div>
                )}
              </div>

              <div className="relative">
                <div className="flex items-center justify-center gap-1 mb-2">
                  <span className="text-xs font-semibold text-slate-600">{t('auth:dispatcherTechnicianPin', 'Dispatcher / Technician PIN')}</span>
                </div>
                <PasswordInput
                  inputMode="numeric"
                  maxLength={4}
                  value={pin}
                  onChange={(e) => handlePinInputChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (pin.length === 4 && /^\d$/.test(e.key)) {
                      e.preventDefault();
                      handlePinInputChange(pin + e.key);
                    }
                  }}
                  placeholder="****"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-center text-2xl tracking-widest"
                  testId="pin-input"
                  autoComplete="off"
                />
              </div>

              <Button 
                type="submit" 
                className="h-12 w-full rounded-xl font-semibold text-base text-white transition disabled:opacity-40"
                style={{ backgroundImage: 'linear-gradient(180deg, #94A3B8 0%, #64748B 100%)', color: '#FFFFFF' }}
                disabled={loading || pin.length !== 4 || !companyCode.trim()}
                data-testid="pin-login-btn"
              >
                {loading && !showEmailForm ? t('auth:verifying', 'Verifying...') : t('auth:loginWithPin', 'Login with PIN')}
              </Button>
            </form>
            
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <span className={`w-full border-t ${cardIsDark ? 'border-slate-700' : 'border-slate-200'}`} />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-transparent text-slate-600 px-2 font-semibold">{t('auth:or', 'Or')}</span>
              </div>
            </div>
            
            {/* Admin Login Section */}
            <div className="space-y-3">
              <div className="flex items-center justify-center gap-1 mb-1">
                <span className="text-xs font-semibold text-slate-600">{t('auth:adminLogin', 'Admin Login')}</span>
              </div>
              
              <Button 
                type="button"
                onClick={() => setShowEmailForm(!showEmailForm)}
                variant="outline"
                className="h-12 w-full rounded-xl font-medium text-base flex items-center justify-center gap-2 hover:brightness-95 transition"
                style={{ backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0', color: '#1E293B', boxShadow: '0 2px 6px rgba(0, 0, 0, 0.05)' }}
                data-testid="toggle-email-form-btn"
              >
                {t('auth:loginWithEmailPassword', 'Login with Email & Password')}
                {showEmailForm ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>

              {showEmailForm && (
                <form onSubmit={handleEmailLogin} className="space-y-4 pt-2 animate-in slide-in-from-top-2 duration-200" data-testid="email-login-form">
                  <div>
                    <Label htmlFor="email" className={cardIsDark ? 'text-slate-300' : 'text-slate-700'}>{t('auth:email', 'Email or NC ID')}</Label>
                    <div className="relative mt-1">
                      <Mail className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${cardIsDark ? 'text-slate-500' : 'text-slate-400'} z-10`} />
                      <Input
                        id="email"
                        type="text"
                        placeholder={t('auth:emailPlaceholder', 'john@company.com or NC001')}
                        value={emailFormData.email}
                        onChange={(e) => setEmailFormData({...emailFormData, email: e.target.value})}
                        className="pl-10"
                        required
                        data-testid="email-input"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="password" className={cardIsDark ? 'text-slate-300' : 'text-slate-700'}>{t('auth:password', 'Password')}</Label>
                    <div className="relative mt-1">
                      <Lock className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${cardIsDark ? 'text-slate-500' : 'text-slate-400'} z-10`} />
                      <PasswordInput
                        placeholder="••••••••"
                        value={emailFormData.password}
                        onChange={(e) => setEmailFormData({...emailFormData, password: e.target.value})}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm pl-10"
                        required
                        testId="password-input"
                        autoComplete="current-password"
                      />
                    </div>
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="h-12 w-full rounded-xl font-semibold text-base text-white hover:brightness-95 transition disabled:opacity-40"
                    style={{ backgroundColor: '#2F5BFF', boxShadow: '0 6px 14px rgba(47, 91, 255, 0.25)' }}
                    disabled={loading}
                    data-testid="email-login-btn"
                  >
                    {loading ? t('loading', 'Signing in...') : t('auth:logIn', 'Sign In')}
                  </Button>
                  
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => navigate('/forgot-password')}
                      className={`text-sm ${cardIsDark ? 'text-slate-500 hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'} hover:underline`}
                      data-testid="forgot-password-link"
                    >
                      {t('auth:forgotPassword', 'Forgot password?')}
                    </button>
                  </div>
                </form>
              )}
            </div>
            
            {/* Sign Up Section — primary CTA matches landing-page wording, Start Over below per request-form pattern */}
            <div className={`text-center space-y-2 mt-6 pt-4 border-t ${cardIsDark ? 'border-slate-700' : 'border-slate-200'}`}>
              <p className="text-sm text-slate-600 font-medium">
                {t('auth:dontHaveAccount', "Don't have an account?")}
              </p>
              <Button
                type="button"
                onClick={() => navigate('/signup')}
                className="h-12 w-full rounded-xl font-semibold text-base text-white hover:brightness-95 transition"
                style={{ backgroundImage: 'linear-gradient(to right, #2563EB 0%, #1D4ED8 50%, #1E40AF 100%)', boxShadow: '0 6px 14px rgba(37, 99, 235, 0.3)' }}
                data-testid="signup-btn"
              >
                <span className="truncate block">{t('auth:startFreeTrial', 'Start Free Trial')}</span>
              </Button>
              <button
                type="button"
                onClick={() => navigate('/landing')}
                data-testid="login-start-over"
                className="mt-3 w-full text-[12px] text-slate-500 hover:text-slate-700 inline-flex items-center justify-center gap-1.5"
              >
                <RotateCcw className="w-3.5 h-3.5" /> Start Over
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
    <NeedHelpStickyBar variant="blue" testId="login-need-help-bar" />
    </>
  );
};

export default AnalyticsLogin;
